import { Injectable } from '@angular/core'
import { NoticeSession } from '@models/planning/notice-session.model'
import { BehaviorSubject } from 'rxjs'

export interface NoticeSessionFloatingPosition {
	x: number
	y: number
}

export interface NoticeSessionFloatingState {
	noticeSession: NoticeSession
	inlineAttached: boolean
	minimized?: boolean
	position?: NoticeSessionFloatingPosition
	/** Visitante (spec 048): o chat destacado continua em leitura, na sala do leitor. */
	viewerMode?: boolean
	readerRoomId?: string | null
}

export interface NoticeSessionFloatingOptions {
	inlineAttached?: boolean
	minimized?: boolean
	position?: NoticeSessionFloatingPosition
	viewerMode?: boolean
	readerRoomId?: string | null
}

@Injectable({ providedIn: 'root' })
export class NoticeSessionChatFloatingService {
	private readonly stateSubject = new BehaviorSubject<NoticeSessionFloatingState | null>(null)
	readonly state$ = this.stateSubject.asObservable()

	enableFloating(noticeSession: NoticeSession, options: NoticeSessionFloatingOptions = {}): void {
		if (!noticeSession) return
		const state: NoticeSessionFloatingState = {
			noticeSession,
			inlineAttached: options.inlineAttached ?? true,
			minimized: options.minimized ?? false,
			position: options.position,
			viewerMode: options.viewerMode ?? false,
			readerRoomId: options.readerRoomId ?? null
		}
		this.stateSubject.next(state)
	}

	openFloatingChat(
		noticeSession: NoticeSession,
		options: Omit<NoticeSessionFloatingOptions, 'inlineAttached'> = {}
	): void {
		this.enableFloating(noticeSession, { ...options, inlineAttached: false })
	}

	disableFloating(): void {
		if (this.stateSubject.value) {
			this.stateSubject.next(null)
		}
	}

	setInlineAttached(attached: boolean): void {
		const current = this.stateSubject.value
		if (!current || current.inlineAttached === attached) return
		this.stateSubject.next({ ...current, inlineAttached: attached })
	}

	setMinimized(minimized: boolean): void {
		const current = this.stateSubject.value
		if (!current) return
		if (current.minimized === minimized) return
		this.stateSubject.next({ ...current, minimized })
	}

	setPosition(position: NoticeSessionFloatingPosition): void {
		const current = this.stateSubject.value
		if (!current) return
		this.stateSubject.next({ ...current, position })
	}

	get snapshot(): NoticeSessionFloatingState | null {
		return this.stateSubject.value
	}
}
